import { fg } from '@confluence/feature-gating';

import type {
	AgentTrustReceipt,
	AgentTrustReceiptScenario,
	AgentTrustReceiptScenarioId,
} from './agentTrustReceiptModel.ts';

const CREATED_BY_AI_LABEL = 'Generated with Rovo';
const EDITED_BY_AI_LABEL = 'Edited with Rovo';
const READ_BY_AI_LABEL = 'Viewed by Rovo';
const SOURCE_LABEL = 'Source';
const EVIDENCE_LABEL = 'Evidence';
const PERSISTENCE_LABEL = 'Persistence';
const NETWORK_CALLS_LABEL = 'Network calls';

const SPARK_RECEIPT_ACTOR: AgentTrustReceipt['actor'] = {
	id: 'rovo-agent-spark-preview',
	name: 'Rovo agent',
	type: 'agent',
};

const SPARK_RECEIPT_SCENARIOS: Record<AgentTrustReceiptScenarioId, AgentTrustReceiptScenario> = {
	CREATED_BY_AI: {
		id: 'CREATED_BY_AI',
		label: CREATED_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo generated this page.',
	},
	EDITED_BY_AI: {
		id: 'EDITED_BY_AI',
		label: EDITED_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo helped edit this page.',
	},
	READ_BY_AI: {
		id: 'READ_BY_AI',
		label: READ_BY_AI_LABEL,
		description: 'Spark preview data indicates Rovo read this page as context.',
	},
};

const SCENARIO_BY_BUCKET: AgentTrustReceiptScenarioId[] = [
	'CREATED_BY_AI',
	'EDITED_BY_AI',
	'READ_BY_AI',
];

const isValidContentId = (contentId: string) => /^[1-9]\d*$/.test(contentId);

const resolveSparkScenarioId = (contentId: string): AgentTrustReceiptScenarioId => {
	const lastDigit = Number(contentId.charAt(contentId.length - 1));
	return SCENARIO_BY_BUCKET[lastDigit % SCENARIO_BY_BUCKET.length];
};

const createSparkPreviewSignals = (
	scenario: AgentTrustReceiptScenario,
	): AgentTrustReceipt['signals'] => [
		{
			id: 'source',
			label: SOURCE_LABEL,
			value: 'Spark preview fixture',
		},
		{
			id: 'evidence',
			label: EVIDENCE_LABEL,
			value: `Example evidence for ${scenario.label.toLowerCase()}`,
		},
		{
			id: 'persistence',
			label: PERSISTENCE_LABEL,
			value: 'None',
		},
		{
			id: 'network',
			label: NETWORK_CALLS_LABEL,
			value: 'None',
		},
	];

export const resolveAgentTrustReceipt = (contentId: string): AgentTrustReceipt | null => {
	if (!fg('cc_sparks_agent_trust_receipt')) {
		return null;
	}

	const normalizedContentId = contentId.trim();

	if (!isValidContentId(normalizedContentId)) {
		return null;
	}

	const scenario = SPARK_RECEIPT_SCENARIOS[resolveSparkScenarioId(normalizedContentId)];

	return {
		contentId: normalizedContentId,
		actor: SPARK_RECEIPT_ACTOR,
		scenario,
		summary: `${scenario.description} This is Spark preview receipt data, not a durable audit log.`,
		signals: createSparkPreviewSignals(scenario),
	};
};
